import type { ExtractPath } from '../types/paths'
import type { AnyFunction } from '../types/utils'
import { getSafePropertyChain } from './get-safe-property-chain'

/**
 * Determines whether the passed value is an Array.
 *
 * @remarks It can be used to narrow the Array's type passing the desired type to the generic.
 * @param value - The value to be checked.
 * @returns True if the value is an Array; otherwise, false.
 */
export function isArray<Type>(value: Type | Type[]): value is Type[] {
  return Array.isArray(value)
}

/**
 * Determines whether the passed value is a Function.
 *
 * @param value - The value to be checked.
 * @returns True if the value is a Function; otherwise, false.
 */
export function isFunction(value: any): value is AnyFunction {
  return Boolean(value) && typeof value === 'function'
}

/**
 * Determines whether the passed value is an Object.
 *
 * @param value - The value to be checked.
 * @returns True if the value is an Object; otherwise, false.
 */
export function isObject(value: any): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

/**
 * Determines whether the passed path is a valid property path of the passed object.
 *
 * @param obj - The object to check the path against.
 * @param path - The path to be checked.
 * @returns `true` if `path` is a valid path of `obj`; otherwise, `false`.
 */
export function isPath<SomeObject, Path extends ExtractPath<SomeObject>>(
  obj: SomeObject,
  path: string,
): path is Path {
  return getSafePropertyChain(obj, path as Path) !== undefined
}
